import type { IState } from "../../helpers";
import { getTimeLeftInDay, MINUTES, offDutyStatuses } from "../../helpers";
import { getWorkdayForDateTime } from "../../hosCalculator";
import {
    DRAINING,
    FROZEN,
    getEmergencyResponseProjection,
    isEmergencyResponseActive,
    type TUnderlyingTrajectory
} from "../../exemptions/emergencyResponse";
import type { TAvailabilityFactor, TComputedAvailabilityResult } from "./shared";
import { resolveEarliestEvent } from "./shared";

export type TAvailabilityFactorEntry = { factor: TAvailabilityFactor; label: string };

export abstract class AvailabilityCalculator {
    /**
     * Returns the date when discrete availability crosses zero. Feeds nextExhaustionEvent only.
     * Must NOT be included in availabilityFactors().
     * Returns undefined when already in exhaustion (no future zero-crossing exists).
     */
    abstract exhaustion(state: IState): Date | undefined;

    /**
     * Returns genuine inflection factors only (rate changes, resets, day boundaries).
     * Must NOT include the exhaustion factor.
     * Returns undefined when this calculator does not apply to the given state
     * (e.g. discreteAvailabilityFunctions.Xxx(state) returns undefined).
     */
    abstract availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined;

    /** Whether availability is currently consuming (rate −1). */
    abstract isConsuming(state: IState): boolean;

    /**
     * How the underlying, unfloored value behaves from now on — the Emergency Response floor's
     * only input beyond the value itself (MYG-139401).
     *
     * Most calculators use `isConsuming === false` to mean "rate 0", and for them this default is
     * exact. A calculator that also uses `false` for "the anchor is unstable, so the rate is
     * unknown" — the state in which it returns an `UNKNOWN_*` coverage gap from
     * `availabilityFactors()` — must override this, or the floor computes its inflection as if the
     * clock were frozen and produces a date that slides with evalTime. Overrides should be as
     * narrow as the evidence: a calculator that can name a sub-state where the rate IS derivable
     * should return the shape for it rather than widening `unknown` over the whole class.
     */
    protected underlyingTrajectory(state: IState): TUnderlyingTrajectory {
        return this.isConsuming(state) ? DRAINING : FROZEN;
    }

    /** Decision-driving state fields for regression-checker diagnostic output. */
    abstract extractConditions(state: IState): Record<string, unknown>;

    /**
     * The raw, unfloored discrete availability this calculator predicts for.
     *
     * MYG-139401 needs it to decide whether the Emergency Response floor is currently governing
     * this type. Each subclass already computes it in conditions(); this just names it for the
     * base class, since the field is called `avail` in most calculators but not all.
     */
    abstract rawAvailability(state: IState): number | undefined;

    /** The time at which the current day boundary passes (midnight of the next calendar day). */
    protected nextDayBoundary(state: IState): Date {
        return new Date(state.nextDate.getTime() + getTimeLeftInDay(state));
    }

    /**
     * When the workday resets if the driver stays in their current off-duty status, or undefined
     * when no reset is approaching: no break anchor, no threshold, or WT that does not count
     * toward consecutive off (the engine zeroes consecutiveOffCount on every tick). Shared by every
     * calculator that predicts the workday reset, so they cannot disagree about when it fires.
     *
     * The anchor is set by countOffDutyTime when the break opens, so it is valid from the break's
     * first instant. The threshold can change once before the reset: on a DayTwo deferral day
     * `workday.offDutyMinimum` carries the deferred time, and tryStartNewDay recomputes it at the
     * next midnight through updateWorkdayOffDutyMinimum (hosCalculator.ts). A reset that would
     * land after that midnight is measured against the recomputed threshold from midnight on, so
     * it fires at max(midnight, anchor + recomputed), which is midnight itself when the break has
     * already run long enough. No other day recomputes the threshold at midnight.
     *
     * A pending ExemptionReducedRest that applies to this break resets on a different threshold;
     * callers check reducedRestResetDate first.
     */
    protected workdayResetDate(state: IState): Date | undefined {
        const anchor = state.workday.consecutiveOffStartTime;
        const offDutyMinimum = state.workday.offDutyMinimum;
        const wtBlocksReset = state.current?.status === "WT" && state.config.workday?.consecutiveOffCountIncludesWt === false;
        if (anchor === undefined || offDutyMinimum === undefined || wtBlocksReset) {
            return undefined;
        }

        const resetTime = anchor.getTime() + offDutyMinimum;
        const midnight = this.nextDayBoundary(state).getTime();
        const configOffDutyMinimum = state.config.workday?.offDutyMinimum;
        if (state.day.deferralStatus !== "DayTwo" || !configOffDutyMinimum || resetTime <= midnight) {
            return new Date(resetTime);
        }

        // Mirrors the non-DayTwo branch of updateWorkdayOffDutyMinimum, which is the one the new
        // day takes: the day after DayTwo is never itself DayTwo.
        const reducedRestUsed = state.workday.reducedRestUsed;
        const reducedRestAdjustment = reducedRestUsed
            ? (state.workday.isExemptionReducedRestApplied ? -reducedRestUsed : reducedRestUsed) * MINUTES
            : 0;
        const recomputedOffDutyMinimum = configOffDutyMinimum + reducedRestAdjustment;
        return new Date(Math.max(midnight, anchor.getTime() + recomputedOffDutyMinimum));
    }

    /**
     * When a pending ExemptionReducedRest log resets the workday out of the current break, or
     * undefined when it does not apply to this break (the plain workdayResetDate governs).
     *
     * Mirrors applyReducedRestAndCalculateWorkdayResetInfo: it only ever considers
     * reducedRestLogs[0] (a log that never applies stays there), and only while the workday has
     * started. getEligibleDurationToReduceRest then looks in the workday containing the log for an
     * eligible period that opened after that workday started and no later than the log. This
     * break's period opens at its anchor and closes when consecutiveOffCount reaches the reduced
     * minimum, so the log applies to this break only if it is in the current workday and was
     * logged at or after the break opened; the reset then lands at anchor + the reduced minimum.
     * A log from before the break, including a stale one from an earlier workday, never applies.
     */
    protected reducedRestResetDate(state: IState): Date | undefined {
        const reducedMinimum = state.config.workday?.exemptionReducedRest?.offDutyMinimum;
        const reducedRestLog = state.reducedRestLogs?.[0];
        const anchor = state.workday.consecutiveOffStartTime;
        const workdayStart = state.workday.start;
        const wtBlocksReset = state.current?.status === "WT" && state.config.workday?.consecutiveOffCountIncludesWt === false;
        if (!reducedMinimum || !state.config.workday?.offDutyMinimum || !reducedRestLog || !state.workday.hasStarted
            || anchor === undefined || workdayStart === undefined || wtBlocksReset) {
            return undefined;
        }
        const logTime = new Date(reducedRestLog.dateTime).getTime();
        const appliesToThisBreak = getWorkdayForDateTime(state, new Date(logTime)) === state.workday
            && anchor.getTime() > workdayStart.getTime()
            && anchor.getTime() <= logTime;
        return appliesToThisBreak ? new Date(anchor.getTime() + reducedMinimum) : undefined;
    }

    /**
     * Returns the Date when a cycle reset will occur (driver's consecutive off reaches
     * cycleRestartMinimum), or undefined if no reset is approaching.
     */
    protected cycleResetDate(state: IState): Date | undefined {
        const { cycleRestartMinimum } = state.config.cycle ?? {};
        const isOffDuty = offDutyStatuses.indexOf(state.current?.status) !== -1;
        if (!isOffDuty || !cycleRestartMinimum || state.cycle.pauseCountingCycleOff
                || state.cycle.cycleConsecutiveOffCount >= cycleRestartMinimum) {
            return undefined;
        }
        const timeUntilReset = cycleRestartMinimum - state.cycle.cycleConsecutiveOffCount;
        return new Date(state.currentDate.getTime() + timeUntilReset);
    }

    compute(state: IState): TComputedAvailabilityResult | undefined {
        const availFactors = this.availabilityFactors(state);
        // MYG-139401: while the Emergency Response floor governs this type the driver is shown the
        // time left in their day, not the real clock, so the floor's own inflection joins the
        // envelope and the type reads as consuming whatever the underlying clock is doing. The
        // subclass factors stay in the map rather than being replaced — an underlying reset can
        // still surface before the floor stops binding, and it would be the earlier inflection.
        // The projection inputs both re-evaluate the subclass's conditions. Check the module's
        // public active-period predicate first so non-exempt calculations avoid that work entirely.
        const rawExhaustion = this.exhaustion(state);
        const exemption = isEmergencyResponseActive(state.emergencyResponsePeriods, state.nextDate)
            ? getEmergencyResponseProjection(state, this.rawAvailability(state), this.underlyingTrajectory(state), rawExhaustion)
            : undefined;
        if (availFactors === undefined && exemption === undefined) return undefined;

        const factors = { ...(availFactors ?? {}) };
        // Unknown trajectories retain the calculator's own UNKNOWN_* coverage gap. Every exact
        // Emergency Response transition is surfaced with its own factor name so a Disabled-log
        // end cannot be confused with the driver's day boundary or a floor release.
        if (exemption?.transition && exemption.transition.date.getTime() > state.nextDate.getTime()) {
            factors[exemption.transition.factorName] = {
                factor: () => exemption.transition!.date,
                label: exemption.transition.label
            };
        }

        const { winner, snapshot } = resolveEarliestEvent(factors);
        const exhaustionDate = exemption ? exemption.exhaustion : rawExhaustion;

        return {
            isConsuming: exemption ? true : this.isConsuming(state),
            nextAvailabilityEvent: winner
                ? { date: winner.date, label: winner.label }
                : undefined,
            nextExhaustionEvent: exhaustionDate
                ? { date: exhaustionDate, label: "exhaustion" }
                : undefined,
            factors: snapshot,
        };
    }
}